import { parse } from 'date-fns';
import {
  FormSectionState,
  FormSectionStateArray,
  FormState,
  StateField,
  StateFieldType,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { DATE_FORMAT } from '@navstation-portal/shared/data-model/time';

export const isString = (value: unknown): value is string => {
  return typeof value === 'string' || value instanceof String;
};

export const isDate = (value: unknown): value is Date => {
  return value instanceof Date;
};

export const getDate = (value: StateFieldType | null | undefined): Date | undefined => {
  if (value == null) return undefined;
  if (isDate(value)) return value;
  if (!isString(value)) return;
  return parse(value, DATE_FORMAT, new Date());
};

export const isNullOrNaN = (value: number | null | undefined) => {
  return value == null || isNaN(value);
};

export const getFieldFromFormState = (formState: FormState, sectionId: string, fieldId: string) => {
  const section = formState[sectionId];
  if (!section) return;
  return section[fieldId];
};

// The state field at a path, or undefined when the form state holds none. A row
// section names the row with `index` and the field in the row with
// `nestedFieldId`.
export const getStateFieldFromFormState = (
  formState: FormState,
  sectionId: string,
  fieldId: string,
  index?: number,
  nestedFieldId?: string
): StateField<StateFieldType> | undefined => {
  const field = getFieldFromFormState(formState, sectionId, fieldId);
  if (!field) return;

  if (isStateField(field)) return field;

  if (index == null || !nestedFieldId) return;

  const entry = field[index];
  if (!entry) return;

  return entry[nestedFieldId];
};

export const getFieldValueFromFormState = (
  formState: FormState,
  sectionId: string,
  fieldId: string,
  index?: number,
  nestedFieldId?: string
) => getStateFieldFromFormState(formState, sectionId, fieldId, index, nestedFieldId)?.value;

export const isStateField = (
  entry: StateField<StateFieldType> | FormSectionStateArray
): entry is StateField<StateFieldType> => {
  if (!entry) return false;
  return (
    (entry as StateField<StateFieldType>).status !== undefined &&
    (entry as StateField<StateFieldType>).pristine !== undefined &&
    (entry as StateField<StateFieldType>).touched !== undefined
  );
};

export const isSectionValid = (section: FormSectionState, fallback = false) => {
  if (!section) return fallback;

  return Object.values(section).every((entry) => {
    if (isStateField(entry)) {
      return entry.status === 'VALID' || entry.status === 'DISABLED';
    }

    return entry.every((nestedEntry) => {
      return Object.values(nestedEntry).every((field) => {
        return field.status === 'VALID' || field.status === 'DISABLED';
      });
    });
  });
};

export const isSectionValidWithOptionalFields = (section: FormSectionState, fallback = false) => {
  if (!section) return fallback;

  return Object.values(section).every((entry) => {
    if (isStateField(entry)) {
      return entry.status !== 'INVALID';
    }

    return entry.every((nestedEntry) => {
      return Object.values(nestedEntry).every((field) => {
        return field.status !== 'INVALID';
      });
    });
  });
};
